import type { ConsignmentAgreement } from './consignment';

export const CONSIGNMENT_TERMS_MAX_CARDS = 20;
export const CONSIGNMENT_TERMS_MAX_HISTORY = 20;
/** Proposed only. No agreement or sale may use this schedule until fee accounting is implemented. */
export interface ProposedConsignmentFeeSchedule {
  kind: 'PROPOSED_FULL_SCHEDULE_V1';
  tiers: Array<{ minimumGrossMinor: number; payoutBps: number }>;
  soldSingleCardFeeMinor: number;
  soldSealedWaxFeeMinor: number;
  unsoldItemFeeMinor: number;
  expressSurchargeMinor: number;
  premiumSurchargeMinor: number;
  selectedItemType: 'SINGLE_CARD' | 'SEALED_WAX' | null;
  selectedService: 'STANDARD' | 'EXPRESS' | 'PREMIUM' | null;
  zeroProceedsFloor: boolean | null;
}
export const PROPOSED_CONSIGNMENT_FEE_SCHEDULE: ProposedConsignmentFeeSchedule = {
  kind: 'PROPOSED_FULL_SCHEDULE_V1',
  tiers: [
    { minimumGrossMinor: 99, payoutBps: 8000 },
    { minimumGrossMinor: 1000, payoutBps: 8200 },
    { minimumGrossMinor: 2500, payoutBps: 8700 },
    { minimumGrossMinor: 50000, payoutBps: 9000 },
    { minimumGrossMinor: 100000, payoutBps: 9600 },
    { minimumGrossMinor: 500000, payoutBps: 9700 },
  ],
  soldSingleCardFeeMinor: 99,
  soldSealedWaxFeeMinor: 999,
  unsoldItemFeeMinor: 200,
  expressSurchargeMinor: 100,
  premiumSurchargeMinor: 500,
  selectedItemType: null,
  selectedService: null,
  zeroProceedsFloor: null,
};
export function proposedConsignmentFeeScheduleIsValid(
  value: unknown,
): value is ProposedConsignmentFeeSchedule {
  if (!value || typeof value !== 'object') return false;
  const schedule = value as ProposedConsignmentFeeSchedule;
  return (
    schedule.kind === 'PROPOSED_FULL_SCHEDULE_V1' &&
    Array.isArray(schedule.tiers) &&
    schedule.tiers.length > 0 &&
    schedule.tiers.length <= 20 &&
    schedule.tiers.every(
      (tier, index) =>
        tier &&
        Number.isSafeInteger(tier.minimumGrossMinor) &&
        tier.minimumGrossMinor >= 0 &&
        Number.isInteger(tier.payoutBps) &&
        tier.payoutBps >= 0 &&
        tier.payoutBps <= 10000 &&
        (index === 0 || tier.minimumGrossMinor > schedule.tiers[index - 1]!.minimumGrossMinor),
    ) &&
    [
      schedule.soldSingleCardFeeMinor,
      schedule.soldSealedWaxFeeMinor,
      schedule.unsoldItemFeeMinor,
      schedule.expressSurchargeMinor,
      schedule.premiumSurchargeMinor,
    ].every((fee) => Number.isSafeInteger(fee) && fee >= 0 && fee <= 100000000) &&
    [null, 'SINGLE_CARD', 'SEALED_WAX'].includes(schedule.selectedItemType) &&
    [null, 'STANDARD', 'EXPRESS', 'PREMIUM'].includes(schedule.selectedService) &&
    (schedule.zeroProceedsFloor === null || typeof schedule.zeroProceedsFloor === 'boolean')
  );
}
export interface ConsignmentTermsDraft {
  feePolicy?: import('./consignment-fees').ConsignmentFeePolicy;
  proposedFeeSchedule?: ProposedConsignmentFeeSchedule;
  draftId: string;
  consignorId: string;
  name: string;
  commissionBps: number | null;
  fixedFeeMinor: number;
  payoutHoldDays: number;
  predecessorAgreementId?: string;
  recordVersion: number;
  status: 'DRAFT' | 'PUBLISHED';
  publishedAgreementId?: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}
export interface CardTermsHistory {
  cardId: string;
  consignorId: string;
  intakeId: string;
  termsRevision: number;
  agreementId: string;
  effectiveAt: string;
  reason: string;
  actor: string;
  requestId: string;
  terms: ConsignmentAgreement;
}
export interface CardTermsDetail {
  cardId: string;
  agreementId: string;
  termsRevision?: number;
  history: CardTermsHistory[];
  revisionEligible: boolean;
  blockedReason?: string;
}
export interface ConsignmentTermsPreview {
  consignorId: string;
  agreement: ConsignmentAgreement;
  cards: Array<{
    cardId: string;
    currentAgreement: ConsignmentAgreement;
    termsRevision: number;
    intakeId: string;
  }>;
  fingerprint: string;
}
export interface ConsignmentTermsApplyResult {
  requestId: string;
  effectiveAt: string;
  agreementId: string;
  cardIds: string[];
}
export type ConsignmentTermsMutation =
  | {
      operation: 'SAVE_TERMS_DRAFT';
      feePolicy?: import('./consignment-fees').ConsignmentFeePolicy;
      proposedFeeSchedule?: ProposedConsignmentFeeSchedule;
      requestId: string;
      draftId: string;
      consignorId: string;
      expectedVersion: number;
      name: string;
      commissionBps: number | null;
      fixedFeeMinor: number;
      payoutHoldDays: number;
      predecessorAgreementId?: string;
    }
  | {
      operation: 'PUBLISH_TERMS_DRAFT';
      requestId: string;
      draftId: string;
      consignorId: string;
      expectedVersion: number;
      termsAgreed: true;
    }
  | { operation: 'PREVIEW_CARD_TERMS'; consignorId: string; agreementId: string; cardIds: string[] }
  | {
      operation: 'APPLY_CARD_TERMS';
      requestId: string;
      consignorId: string;
      agreementId: string;
      cardIds: string[];
      fingerprint: string;
      reason: string;
    };
